import type { ComputedRef, Ref } from 'vue';
import { computed } from 'vue';
import type { Ticket } from '@/interface/ITicket';
import type { PlayerType } from '@/interface/enum';
import { SingleModeBalanceError } from '@/interface/enum';
import {
    getAffordableTicketStake,
    getTicketRequiredBalance,
    getTotalTicketRequiredBalance,
} from '@/libraries/betSlip/ticketHelper';
import { useBetSlipBalance } from '@/libraries/composables/useBetSlipBalance';

export interface ISingleModeBalanceInput {
    tickets: ComputedRef<Ticket[]>;
    isActive: ComputedRef<boolean>;
    isVoucherRedemptionEnabled: Ref<boolean>;
    balance: Ref<number | null>;
    isShowDepositInTicketEnabled: Ref<boolean>;
    playerType: ComputedRef<PlayerType>;
}

export function useSingleModeBalance(input: ISingleModeBalanceInput) {
    const {
        tickets,
        isActive,
        isVoucherRedemptionEnabled,
        balance,
        isShowDepositInTicketEnabled,
        playerType,
    } = input;

    const ticketsWithEnteredStake = computed(() => tickets.value.filter(ticket => (
        !ticket.isPendingRemoval
        && ticket.openTicketError === null
        && ticket.minBet > 0
        && ticket.stake > 0
    )));
    const enteredTicketCount = computed(() => ticketsWithEnteredStake.value.length);
    const singleEnteredBalanceTicket = computed(() => (
        enteredTicketCount.value === 1
            ? ticketsWithEnteredStake.value[0]
            : null
    ));
    const singleEnteredMinimumRequiredBalance = computed(() => {
        const ticket = singleEnteredBalanceTicket.value;
        return ticket === null
            ? null
            : getTicketRequiredBalance(ticket, ticket.minBet);
    });
    const totalRequiredBalance = computed(() => getTotalTicketRequiredBalance(ticketsWithEnteredStake.value));
    const singleEnteredAffordableStake = computed(() => {
        const ticket = singleEnteredBalanceTicket.value;
        return ticket === null || balance.value === null
            ? null
            : getAffordableTicketStake(ticket, balance.value);
    });
    const {
        isBalanceBelowMinimumRequired: isBalanceBelowEnteredTicketMinimum,
        isRequiredBalanceExceeded: isTotalRequiredBalanceExceeded,
        isRequiredBalanceAdjustable: isSingleStakeBalanceAdjustable,
        shouldShowDeposit,
    } = useBetSlipBalance({
        isActive,
        isEnabled: computed(() => !isVoucherRedemptionEnabled.value),
        hasEnteredStake: computed(() => enteredTicketCount.value > 0),
        requiredBalance: totalRequiredBalance,
        minimumRequiredBalance: singleEnteredMinimumRequiredBalance,
        affordableStake: singleEnteredAffordableStake,
        minimumStake: computed(() => singleEnteredBalanceTicket.value?.minBet ?? null),
        balance,
        isShowDepositInTicketEnabled,
        playerType,
    });
    const isSingleStakeBalanceExceeded = computed(() => (
        !isBalanceBelowEnteredTicketMinimum.value
        && isTotalRequiredBalanceExceeded.value
        && enteredTicketCount.value === 1
    ));
    const isMultipleStakeBalanceExceeded = computed(() => (
        isTotalRequiredBalanceExceeded.value
        && enteredTicketCount.value > 1
    ));
    const balanceError = computed<SingleModeBalanceError | null>(() => {
        if (isBalanceBelowEnteredTicketMinimum.value) {
            return SingleModeBalanceError.BelowMinimumStake;
        }
        if (isSingleStakeBalanceExceeded.value) {
            return SingleModeBalanceError.SingleStakeExceedsBalance;
        }
        if (isMultipleStakeBalanceExceeded.value) {
            return SingleModeBalanceError.MultipleStakesExceedBalance;
        }
        return null;
    });

    return {
        ticketsWithEnteredStake,
        enteredTicketCount,
        singleEnteredMinimumRequiredBalance,
        totalRequiredBalance,
        isBalanceBelowEnteredTicketMinimum,
        isSingleStakeBalanceExceeded,
        isMultipleStakeBalanceExceeded,
        isSingleStakeBalanceAdjustable,
        shouldShowDeposit,
        balanceError,
    };
}
